// src/pages/ContentEditor/FlowchartEditor.js
import React, { useState, useRef, useCallback } from "react";
import ReactFlow, {
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  ReactFlowProvider,
} from "reactflow";
import html2canvas from "html2canvas";
import "reactflow/dist/style.css";

// Custom node component to support text editing and resizing
const CustomNode = ({ data, id, selected }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [text, setText] = useState(data.label || "Double-click to edit");

  const handleDoubleClick = () => {
    setIsEditing(true);
  };

  const handleChange = (e) => {
    setText(e.target.value);
  };

  const handleBlur = () => {
    setIsEditing(false);
    // onNodeChange functionality removed as it's currently unused
  };

  // Define styles based on node type
  const getNodeStyle = (type) => {
    const baseStyle = {
      padding: "10px",
      border: selected ? "2px solid #1a73e8" : "1px solid #777",
      background: "#fff",
      minWidth: "100px",
      minHeight: "50px",
      resize: "both",
      overflow: "auto",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
    };

    switch (type) {
      case "rectangle":
        return { ...baseStyle, borderRadius: "5px" };
      case "diamond":
        return {
          ...baseStyle,
          transform: "rotate(45deg)",
          transformOrigin: "center",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        };
      case "oval":
        return { ...baseStyle, borderRadius: "50%" };
      default:
        return baseStyle;
    }
  };

  // Adjust text rotation for diamond shape
  const textStyle = data.type === "diamond" ? { transform: "rotate(-45deg)" } : {};

  return (
    <div
      style={getNodeStyle(data.type)}
      onDoubleClick={handleDoubleClick}
    >
      {isEditing ? (
        <textarea
          value={text}
          onChange={handleChange}
          onBlur={handleBlur}
          autoFocus
          style={{
            width: "100%",
            height: "100%",
            border: "none",
            resize: "none",
            fontSize: "14px",
            textAlign: "center",
            ...textStyle,
          }}
        />
      ) : (
        <div style={textStyle}>{text}</div>
      )}
    </div>
  );
};

// Define node types
const nodeTypes = {
  rectangle: CustomNode,
  diamond: CustomNode,
  oval: CustomNode,
  input: CustomNode,
  output: CustomNode,
};

const FlowchartEditor = ({ onInsert, onCancel }) => {
  const reactFlowWrapper = useRef(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const [flowchartName, setFlowchartName] = useState("Untitled Flowchart");

  // Update node data (e.g., label) when edited - currently unused
  // const onNodeChange = useCallback(
  //   (id, newData) => {
  //     setNodes((nds) =>
  //       nds.map((node) => {
  //         if (node.id === id) {
  //           return { ...node, data: { ...node.data, ...newData } };
  //         }
  //         return node;
  //       })
  //     );
  //   },
  //   [setNodes]
  // );

  // Handle new connections between nodes
  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  );

  // Add new node on drag and drop
  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();

      if (!reactFlowInstance) return;

      const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
      const type = event.dataTransfer.getData("application/reactflow");

      // Check if the dropped element is valid
      if (typeof type === "undefined" || !type) {
        return;
      }

      const position = reactFlowInstance.project({
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top,
      });

      const newNode = {
        id: `node-${nodes.length + 1}`,
        type: type === "default" ? "rectangle" : type, // Default to rectangle if type is "default"
        position,
        data: { label: `Node ${nodes.length + 1}`, type: type === "default" ? "rectangle" : type },
      };

      setNodes((nds) => nds.concat(newNode));
    },
    [reactFlowInstance, nodes, setNodes]
  );

  // Handle save/insert flowchart as image
  const handleInsert = () => {
    if (!reactFlowWrapper.current) return;

    // Use html2canvas to capture the flowchart as an image
    html2canvas(reactFlowWrapper.current, {
      backgroundColor: "#fff",
      scale: 2, // Increase resolution
    }).then((canvas) => {
      const imageData = canvas.toDataURL("image/png");
      const filename = `${flowchartName.replace(/\s+/g, "-").toLowerCase()}.png`;

      if (onInsert) {
        onInsert({ imageData, filename });
      }
    });
  };

  return (
    <div className="flowchart-editor-container h-full flex flex-col">
      <div className="flex justify-between items-center mb-4 p-2 bg-gray-50 border-b">
        <input
          type="text"
          value={flowchartName}
          onChange={(e) => setFlowchartName(e.target.value)}
          className="p-2 border rounded"
          placeholder="Flowchart name"
        />
        <div className="flex space-x-2">
          <button
            onClick={onCancel}
            className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
          >
            Cancel
          </button>
          <button
            onClick={handleInsert}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Insert Flowchart
          </button>
        </div>
      </div>

      <div className="flex-1" ref={reactFlowWrapper}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onInit={setReactFlowInstance}
          onDrop={onDrop}
          onDragOver={onDragOver}
          nodeTypes={nodeTypes}
          fitView
        >
          <Controls />
          <Background variant="dots" gap={12} size={1} />
        </ReactFlow>
      </div>

      <div className="node-palette p-2 bg-gray-50 border-t">
        <h3 className="text-sm font-medium mb-2">Shapes</h3>
        <div className="flex space-x-2 flex-wrap gap-2">
          <div
            className="node-item p-2 border rounded cursor-grab bg-white"
            draggable
            onDragStart={(event) => {
              event.dataTransfer.setData("application/reactflow", "rectangle");
            }}
          >
            Rectangle
          </div>
          <div
            className="node-item p-2 border rounded cursor-grab bg-white"
            draggable
            onDragStart={(event) => {
              event.dataTransfer.setData("application/reactflow", "diamond");
            }}
          >
            Diamond
          </div>
          <div
            className="node-item p-2 border rounded cursor-grab bg-white"
            draggable
            onDragStart={(event) => {
              event.dataTransfer.setData("application/reactflow", "oval");
            }}
          >
            Oval
          </div>
          <div
            className="node-item p-2 border rounded cursor-grab bg-white"
            draggable
            onDragStart={(event) => {
              event.dataTransfer.setData("application/reactflow", "input");
            }}
          >
            Start
          </div>
          <div
            className="node-item p-2 border rounded cursor-grab bg-white"
            draggable
            onDragStart={(event) => {
              event.dataTransfer.setData("application/reactflow", "output");
            }}
          >
            End
          </div>
        </div>
      </div>
    </div>
  );
};

// Wrap with ReactFlowProvider to ensure proper context
const FlowchartEditorWithProvider = (props) => (
  <ReactFlowProvider>
    <FlowchartEditor {...props} />
  </ReactFlowProvider>
);

export default FlowchartEditorWithProvider;